import { useEffect, useRef, useState } from "react";
import CssBaseline from '@mui/material/CssBaseline';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import ClearIcon from '@mui/icons-material/Clear';

import BodyConfigList from "../components/SystemEditor/BodyConfigList";
import BodyConfigControls from "../components/SystemEditor/BodyConfigControls";
import TimeSettingsControls from "../components/TimeSettingsControls";
import BodyConfigUploadButton from "../components/SystemEditor/BodyConfigUploadButton";
import SunConfigUploadButton from "../components/SystemEditor/SunConfigUploadButton";
import OrbitDisplay from "../components/Display/OrbitDisplay";
import SelectProvidedConfigs from "../components/SystemEditor/SelectProvidedConfigs";
import GetLinkButton from "../components/SystemEditor/GetLinkButton";
import DashboardPanel from '../components/DashboardPanel';
import Navbar from '../components/Navbar';
import HelpCollapse from '../components/Transfer/HelpCollapse';
import InfoPopper from "../components/Display/InfoPopper";
import { NumberField } from "../components/NumberField";

import SolarSystem from "../main/objects/system";

import { atom, useAtom } from "jotai";
import { systemAtom, customSystemAtom, systemNameAtom, systemScaleAtom } from "../App";

function SolarSystemAppContent() {
  const [system] = useAtom(systemAtom);
  const [customSystem, setCustomSystem] = useAtom(customSystemAtom);
  const [systemName] = useAtom(systemNameAtom);
  const [systemScale, setSystemScale] = useAtom(systemScaleAtom);

  const [centralBody, setCentralBody] = useState(customSystem.sun);
  const centralBodyName = useRef(customSystem.sun.name);

  const [deleteBodiesTrigger, setDeleteBodiesTrigger] = useState(0);

  const [showHelp, setShowHelp] = useState(false);
  const infoItemAtom = useRef(atom<InfoItem>(null)).current;
  const canvasRef = useRef<HTMLDivElement | null>(null);

  // UI State
  const [leftOpen, setLeftOpen] = useState(true);
  const [midOpen, setMidOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);

  const bodyOptions = [...customSystem.orbiterIds.values()].map((body) => (
    <MenuItem key={body.id} value={body.name}>{body.name}</MenuItem>
  ));
  bodyOptions.unshift(<MenuItem key={customSystem.sun.id} value={customSystem.sun.name}>{customSystem.sun.name}</MenuItem>);

  useEffect(() => {
    if (systemName !== 'Custom System') {
      setCustomSystem(system);
      setCentralBody(system.sun);
      centralBodyName.current = system.sun.name;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [system]);

  useEffect(() => {
    if (deleteBodiesTrigger > 0) {
      const sunConfig = customSystem.sun;
      const newSystem = new SolarSystem(sunConfig, [], false);
      setCustomSystem(newSystem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteBodiesTrigger]);

  ///// App Body /////
  return (
    <div style={{ width: '100vw', height: '100vh', position: 'relative', overflow: 'hidden' }}>
      <CssBaseline />
      
      {/* Background Layer: 3D Orbit Display */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        <div ref={canvasRef} style={{ width: '100%', height: '100%' }}>
          <OrbitDisplay 
            label='Custom System'
            index={0}
            centralBody={centralBody} 
            startDate={0.0} 
            system={customSystem}
            infoItemAtom={infoItemAtom}
            fullScreen={true}
          />
        </div>
        <InfoPopper infoItemAtom={infoItemAtom} parentRef={canvasRef} system={customSystem} />
      </div>

      {/* UI Overlay Layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none', display: 'flex', flexDirection: 'column' }}>
        <Navbar showHelp={showHelp} setShowHelp={setShowHelp} />
        
        {/* Floating Header */}
        <div style={{ margin: '16px 32px', pointerEvents: 'auto' }}>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 800, textShadow: '0 0 10px rgba(0, 243, 255, 0.5)' }}>
            SOLAR SYSTEM EDITOR
          </Typography>
          <HelpCollapse showHelp={showHelp} />
        </div>

        {/* Dashboard Panels */}
        <DashboardPanel 
          side="left" 
          title="System Registry" 
          isOpen={leftOpen} 
          onToggle={() => setLeftOpen(!leftOpen)}
          width={320}
        >
          <Stack spacing={2}>
            <SelectProvidedConfigs />
            <NumberField 
              label="System Scale Factor"
              value={systemScale}
              setValue={setSystemScale}
              min={0}
              error={systemScale <= 0}
            />
            <Divider />
            <Stack spacing={1} alignItems='center'>
              <SunConfigUploadButton />
              <BodyConfigUploadButton />
              <Button
                variant="text" 
                color="inherit" 
                startIcon={<ClearIcon />}
                onClick={() => {
                  setCentralBody(customSystem.sun);
                  centralBodyName.current = customSystem.sun.name;
                  setDeleteBodiesTrigger(deleteBodiesTrigger + 1);
                }}
              >
                Clear Bodies
              </Button>
            </Stack>
            <Divider />
            <BodyConfigList />
          </Stack>
        </DashboardPanel>

        <DashboardPanel 
          side="center" 
          title="Celestial Body Config" 
          isOpen={midOpen} 
          onToggle={() => setMidOpen(!midOpen)}
          width={350}
        >
          <BodyConfigControls />
        </DashboardPanel>

        <DashboardPanel
          side="right"
          title="System Settings"
          isOpen={rightOpen}
          onToggle={() => setRightOpen(!rightOpen)}
          width={320}
        >
          <Stack spacing={2}>
            <GetLinkButton />
            <Divider />
            <FormControl fullWidth variant="filled">
              <InputLabel id={"body-select-label"}>Focus Body</InputLabel>
              <Select
                  labelId={"body-select-label"}
                  id={'central-body'}
                  value={centralBodyName.current}
                  onChange={(e) => {
                    centralBodyName.current = (e.target.value as string);
                    setCentralBody(customSystem.bodyFromName(e.target.value as string));
                  }}
              >
                  {bodyOptions}
              </Select>
            </FormControl>
            <Divider />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>TIME CONFIGURATION</Typography>
            <TimeSettingsControls />
          </Stack>
        </DashboardPanel>
      </div>
    </div>
  )
}

function SolarSystemApp() {
  return <>{SolarSystemAppContent()}</>
}

export default SolarSystemApp;
